import * as modAjax from "./modAjax";
import * as modArchive from "./modArchive";
import * as modLocale from "./modLocale";
import * as modPlayer from "./modPlayer";


export class Result<T> {
    result: T;
    status: "ok" | "error";

	constructor(result: T, status: 'ok'|'error' = 'ok') {
		this.result = result;
		this.status = status;
	}
}

// result of GET /player/state
export class AppState {
	capabilities: {
		instantSearch?: boolean;
		jingleScheduler?: boolean;
		schedulerDates?: boolean;
		schedulerSeconds?: boolean;
		schedulerYearly?: boolean;
		showMusicArchive?: boolean;
		showPlayerVolume?: boolean;
		showScrollButtons?: boolean;
		songPreviews?: boolean;
	} = {}

	// TODO add getPlayer()/listPlayers() which return a neat data class (with .name, .streamUrl, ...)
	players: {[id: string]: string};
	// TODO playerOrder?: string[];
	streamURLs: {[id: string]: string};
	user?: {
		name?: string;
		role?: string;
		admin?: boolean;
		canEditScheduler?: boolean;
		canSeeAllPlayers?: boolean;
		canLogout?: boolean; // depends on the login method
		showSettingsButton?: boolean;
	}
}

export var ERT_MIMETYPE = "application/x-ert-entities";

// data sent via drag'n'drop (of type ERT_MIMETYPE)
export type DraggedList = DraggedItem[];

export class DraggedItem {
	type?: string;
	id?: string;
}

// returns "type::id" (e.g. 'song::12345')
// throws an exception if one of them is null or undefined
// already qualified ids are left as-is (e.g. id='song::123' will return 'song::123' or raise an exception if there's a type mismatch)
export function entityId(type: string|undefined, id: number|string|undefined): string {
	if (type == undefined) throw "entityId: missing type";
	if (id == undefined) throw "entityId: missing id";

	if (type == 'playlist') type = 'pl';

	let parts = id.toString().split(":");
	if (parts.length == 1 && !isNaN(parseInt(id.toString()))) {
		// unqualified, numeric id -> prepend type
		return `${type}::${id}`;
	}

	// qualified id -> complain if the type doesn't match (and simply return the unmodified value otherwise)
	if (parts[0] != type) throw `entityId: type mismatch: type='${type}', id='${id}'`;
	return id.toString();
}

export function unentityid(qualifiedId: string) {
	if (!qualifiedId) throw "unentityid: missing id"
	var parts = qualifiedId.split('::');
	if (parts.length != 2) throw `unentityid: malformed id: ${qualifiedId}`
	var type:string = parts[0];
	var id = parseInt(parts[1]);
	return {
		'type': type,
		'id': id,
	}
}

export function hideDialog($elem: JQuery|string) {
	if (typeof($elem) == 'string') $elem = $($elem);
	var $modalContainer = $elem.parents('.modalContainer');;
	$modalContainer.fadeOut(200);
}

export function showDialog(elem: string): JQuery<HTMLDivElement> {
	var $elem = $(elem) as JQuery<HTMLDivElement>;
	var $modalContainer = $elem.parents('.modalContainer');;

	$modalContainer.find('> .modalContent').hide();
	$elem.show();
	$modalContainer.find('.cancel').off('click').on('click', () => {
		hideDialog($elem);
	});
	$modalContainer.fadeIn(200);
	return $elem;
}



/**
 * error callback function
 *
 * the default implementation will show the #loginDialog on 403/401 errors and pop up an #errorMsg overlay for everything else
 *
 * code may be a HTTP error code or 0
 *
 * if msg is empty, the error has been reset/resolved
 */
type ErrorCallback = (err: ErrorMessage, keepVisible: boolean) => void

var _onErrorCallback: ErrorCallback = _onErrorDefault;
function _onErrorDefault(err: ErrorMessage, keepVisible:boolean) {
	var $popup = $('#errorMsg');
	if (err.msg != null) {
		if (console) console.log(err.msg);
		$popup.text(err.msg);
		$popup.show().css('opacity', 1);
		$popup.stop();
	}
	else {
		$popup.hide();
	}

	if (keepVisible !== true) {
		$popup.show().delay(1000).animate({'opacity': 0}).hide(0);
	}
}

export function setErrorCallback(cb: ErrorCallback) {
	_onErrorCallback = cb
}

export class ErrorMessage {
	code: number
	msg: string
	details: string|null

	constructor(code: number, msg: string) {
		this.code = Number(code);
		this.msg = msg.toString();
		this.details = null;
	}

	setDetails(details: string) { this.details = details; return this; }

	/// show message and fade out after 1sec (in the default implementation; override the way errors are displayed using setErrorCallback())
	show() { return this._show(false); }

	/// show message and keep visible (override the way errors are displayed using setErrorCallback())
	showPersistent() { return this._show(true); }

	_show(keepVisible: boolean) {
		// todo make an onHttpError() for move in ajax error handlers and move the status code check there
		if (this.code == 401 || this.code == 403) { // TODO 2024-01-22: remove 403 here some time in the future (note that that may break old players' login) - we'd have to remove the same code==403 check from modAjax's _onError()
			$('body').addClass('loggedOut');
			showDialog('#loginDialog');
			$('#loginUser').trigger('focus');
			return;
		}

		return _onErrorCallback(this, keepVisible);
	}
}


/*****************************/
/* drag+drop module          */
/*****************************/
export function setDropHandler($elem: JQuery<HTMLElement>, fn: (ev: JQuery.DropEvent) => void) {
	$elem = $($elem); // TODO this line is backwards compatibility code - remove it once all callers are upgraded
	var canDrop = function() {
		return !$elem.data('nodrop');
	};
	$elem.on('dragenter', function(ev) {
		if (canDrop()) {
			//$elem.css('border-radius', '10px 10px');
			$elem.addClass('dropTarget');
			ev.preventDefault();
		}
	}).on('dragover', function(ev) {
		if (canDrop()) {
			ev.preventDefault();
		}
	}).on('dragleave', function(_ev) {
		$elem.removeClass('dropTarget');
	}).on('drop', function(ev) {
		if (canDrop()) {
			$elem.removeClass('dropTarget');
			fn(ev);
		}
	});
}

export function formatDuration(secs: number|undefined, type?: string) {
	if (secs == undefined || secs < 0 || type === 'stream') return '-:--';
	secs = Math.floor(secs);
	var mins = Math.floor(secs / 60);

	var ss = zeroPad(secs%60, 2);
	var mm = mins.toString();
	if (mins >= 120) {
		var hours = Math.floor(mins / 60);
		mm = zeroPad(mins%60, 2);
		return hours+"h"+(secs %2 ? '.' : ' ')+mm+"m";
	}
	else return mm+":"+ss;
}

export function formatDurationMsec(msec: number|undefined, type?: string) {
	if (msec == undefined || msec < 0 || type === 'stream') return '-:--';
	return formatDuration(msec/1000);
}

export function changeVolume(newVolume: number) {
	modAjax.post('/player/'+modPlayer.getId()+'/volume', {"volume": newVolume}).then(modPlayer.gotStatus);
}

var _appState: AppState|null = null;
var _appStatePromise:JQuery.Deferred<any,any,any> = null; // there should only be one request running at any time
export function getAppState(reload = false): JQueryDeferred<AppState> {
	if (reload || _appState == null) {
		// decide if we need to (re)fetch the app state
		if (_appStatePromise == null || reload) {
			_appStatePromise = $.Deferred();

			modAjax.request('/player/state').then((data: Result<AppState>) => {
				if (data.result.user == undefined) data.result.user = {};
				_appStatePromise.resolve(data.result);
			}, () => {
				_getLegacyAppState().then((rc) => {
					_appStatePromise.resolve(rc);
				}, (jqXHR, textStatus, errorThrown) => {
					_appStatePromise.reject(jqXHR, textStatus, errorThrown);
				});
			},);
		}

		_appStatePromise.then((state: AppState) => {
			_appState = state;
			$('#archiveRoot').css({'display': state.capabilities.showMusicArchive ? '' : 'none'});
			$('#volumeCell').css({'display': state.capabilities.showPlayerVolume ? '' : 'none'});
			$('body').toggleClass('can-preview', state.capabilities.songPreviews === true);
			$('body').toggleClass('withSchedulerDates', state.capabilities.schedulerDates === true);

			// show scroll buttons if not explitly set to hide
			$('.scrollBtns').css('display', state.capabilities.showScrollButtons ? '' : 'none');
			$('body').toggleClass('withSettingsBtn', state.user.showSettingsButton === true);
		});

		return _appStatePromise;
	}
	else return $.Deferred().resolve(_appState);
}

function _getLegacyAppState(): JQueryDeferred<AppState> {
	// 2022-09-16: legacy code
	// for clients that lack the new /player/state endpoint we need to collect the data from different places.
	// this function does that and transforms the data we expect in AppState

	let promise = $.Deferred();
	let rc = new AppState();
	let requests = $.when(modAjax.request('/settings/state').then((data: Result<{[id: string]: boolean}>) => {
		let result = data.result;
		if (result['archive.show']) rc.capabilities.showMusicArchive = true;
		if (result['scrollButtons.show']) rc.capabilities.showScrollButtons = true;
		if (result['search.instant']) rc.capabilities.instantSearch = true;
		if (result['volume.enabled']) rc.capabilities.showPlayerVolume = true;
		if (result['web.previews']) rc.capabilities.songPreviews = true;
	}), modAjax.request('player/list').then((data: Result<any>) => {
			rc.players = data.result.players;
	}));

	requests.then(() => {
		promise.resolve(rc);
	}, (jqXHR, textStatus, errorThrown) => {
		promise.reject(jqXHR, textStatus, errorThrown);
	});

	return promise;
}

export function savePlaylist() {
    var $dlg = showDialog("#savePlDialog");
    var $nameInput = $('input', $dlg);
    var folderId: number|null = null; // null means parentDir=root

    function gotArchive(data: modArchive.ArchiveResult) {
        var $ul = $('ul.plDir', $dlg).empty() as JQuery<HTMLUListElement>;
        hideDialog('#mkdirDialog'); // does nothing unless called as result of showMkdir()
        var userPlaylists = data.result.userPlaylists;
		if (userPlaylists==undefined) throw "missing userPlaylists";
        userPlaylists.name = "My Eartunes";
        userPlaylists.id = undefined; // have the parentId undefined if the users selects the root dir
        addDirsRec(userPlaylists, $ul, true).attr('data-tr', 'archive.userPlaylists');
        $nameInput.data('folderId', undefined);
        modLocale.set(undefined, $ul);
        $('#loadingAnim').hide();
    }

    $nameInput.val('').data('folderId', '');
    function addDirsRec(data: modArchive.Entry, $ul: JQuery<HTMLUListElement>, expanded: boolean) {
		if (data.type == undefined) throw "savePlaylist: missing archive entry type";
        var $li = $('<li>');
        var $titleDiv = $('<div>');
        $titleDiv.text(data.name||"ERROR").data('type', data.type)
		if (data.id !== undefined) $titleDiv.data('id', data.id);

        if (data.type == 'folder') {
            $titleDiv.addClass('folder').on('click', () => {
                $('~ul', $titleDiv).toggleClass('hidden');
                $('.selected', $dlg).removeClass('selected');
                $titleDiv.addClass('selected');
                folderId = $titleDiv.data('id');
                $nameInput.data('folderId', $titleDiv.data('id'));
            });
        }
        $li.append($titleDiv);
        if (data.children && data.children.length > 0) {
            var $subUl = $('<ul>') as JQuery<HTMLUListElement>;
            if (!expanded) $subUl.addClass('hidden');
            $li.append($subUl);
            for (var i = 0; i < data.children.length; i++) {
                addDirsRec(data.children[i], $subUl, false);
            }
        }
        $ul.append($li);
        return $titleDiv;
    }

    $('#loadingAnim').show();
    modArchive.getArchive().then(gotArchive);

    var $savePlBtn = $dlg.find('.savePlBtn');
    $savePlBtn.off('click').on('click', () => {
        if (!$nameInput.val()) return;
        $('#loadingAnim').show();
        var data = {
            name: $nameInput.val(),
            folderId: undefined,
        };
        var folderId = $nameInput.data('folderId');
        if (folderId !== null) data.folderId = folderId;
        modAjax.post('/player/'+modPlayer.getId()+'/playlist/save?radio=true', data).then((data) => {
            modArchive.gotArchive(data);
            $('#loadingAnim').hide();
        });
        $nameInput.val('').data('folderId', '');
        hideDialog($dlg);
    });

    function checkPlName() {
        $savePlBtn.attr('disabled', $nameInput.val() ? null : 'disabled');
    }
    $nameInput.off('keyup').on('keyup', checkPlName);
    checkPlName();

    $('.mkdirBtn', $dlg).off('click').on('click', () => {
        showMkdirDlg(folderId, gotArchive);
    });
}

export function showOrHidePlayerList(playerCount: number) {
	// add/remove the player selector (by setting the '.multiroom' class on the body element)
	if (playerCount > 1) { // multiroom
		$('body').addClass('multiroom');
	}
	else $('body').removeClass('multiroom');
}


export function showMkdirDlg(parentId: number|null, gotArchive: (data: modArchive.ArchiveResult) => any) {
	var $dlg = showDialog('#mkdirDialog');
	$('input', $dlg).val('').trigger('focus');

	var $okBtn = $('.okBtn', $dlg);
	var $nameInput = $('.mkdirInput', $dlg);
	$okBtn.off('click').on('click', () => {
		var name = $nameInput.val() as string;
		if (name.length == 0) return;

		var data = {"name": name, 'parentId': null as number|null};
		if (parentId != null) data.parentId = parentId;
		modAjax.post('/archive/playlist/mkdir', data).then(gotArchive);
	});

	function checkName() {
		$okBtn.attr('disabled', $nameInput.val() ? null : 'disabled');
	}
	$nameInput.off('keyup').on('keyup', checkName);
	checkName();
}


export function zeroPad(num: number, size: number): string {
	var rc = num.toString();
	while (rc.length < size) rc = "0" + num;
	return rc;
}


$(function() {
	modLocale.set();

	// disable right click
/*	$('body').bind('contextmenu', function(e) {
		e.preventDefault();
	});*/
});
